<template>
    <div>
        <div class="flex justify-between items-center border-b border-gray-200 pb-2 mb-3">
            <h1 class="module-header">Key Dates</h1>
            <div v-if="! showSiteData && leaseCount" @click="manageKeyDates">
                <button class="ml-3 flex items-center btn btn-small btn-indigo">
                    <inline-svg name="edit" classes="w-4 h-4 text-white fill-current"></inline-svg>
                    <span class="ml-2">Manage Key Dates</span>
                </button>
            </div>
        </div>

        <div v-if="keyDateCount">
            <div v-for="(row, rowIndex) in keyDates" :key="rowIndex" class="mb-10 pt-5 pb-6 px-6 bg-gray-50 border shadow space-y-8">
                <div v-for="(lease, leaseIndex) in row.leases" :key="leaseIndex">
                    <div class="flex items-center">
                        <div v-if="showSiteData" class="pr-4 border-r border-dashed border-gray-500">
                            <a v-bind:href="row.site.view_url" class="flex items-center">
                                <div class="relative">
                                    <div v-if="row.site.archived_at !== null" class="absolute top-0 left-0 pt-1 pl-1 z-40" title="Site is Archived">
                                        <inline-svg name="archived" classes="w-2 h-2 text-white fill-current"></inline-svg>
                                    </div>
                                    <img class="w-10 h-10 rounded-sm shadow-md mr-2" :src="row.site.building.image.view_url">
                                </div>
                                <div class="flex flex-col">
                                    <p class="text-lg font-semibold">{{ row.site.building.address_line_1 }}</p>
                                    <p class="text-base">{{ row.site.building.city }}, {{ row.site.building.state }}</p>
                                </div>
                            </a>
                        </div>
                        <div class="text-lg text-gray-900" :class="{ 'pl-4': showSiteData }">{{ lease.name }}</div>
                    </div>
                    <table v-if="lease.dates.length" class="mt-2" :class="fromReatlas ? 'w-full' : 'lg:w-3/4'">
                        <thead class="cursor-pointer">
                            <tr>
                                <th class="text-center">
                                    <div class="flex items-center justify-center gap-1">
                                        Clause
                                        <InfoIcon identifier="sites-keydatestabs-clause" />
                                    </div>
                                </th>
                                <th class="text-center">Occurs On</th>
                                <th>Type</th>
                                <th class="text-center">Status</th>
                                <th :class="{ 'w-1/3': ! showSiteData }">Note</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(date, dateIndex) in lease.dates" :key="dateIndex">                  
                                <td class="text-center">{{ date.lease_clause }}</td>
                                <td class="text-center">{{ date.occurs_on | date }}</td>
                                <td>{{ date.type }}</td>
                                <td class="text-center font-variant-all-small-caps">
                                    <div v-if="date.completed_at == null">
                                        <span class="badge badge-red">Incomplete</span>
                                    </div>
                                    <div v-else class="leading-normal">
                                        <span class="badge badge-green">Completed</span>
                                    </div>
                                </td>
                                <td class="leading-normal">
                                    <v-popover v-if="date.note" trigger="hover click" offset="10">
                                        <inline-svg name="note"></inline-svg>

                                        <template slot="popover">
                                            <div class="leading-normal max-w-sm p-6">
                                                {{ date.note }}
                                            </div>
                                        </template>
                                    </v-popover>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else>
                        <p class="none-found text-left" :class="{ 'mt-5': showSiteData }">No Key Dates Found</p>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <p class="none-found text-left">No Key Dates Found</p>
        </div>

        <modal name="manage-key-dates" width="60%" height="auto" click-to-close draggable=".modal-header">
            <manage-leases selectedSection="key-dates"></manage-leases>
        </modal>
    </div>
</template>

<script>   

    export default {
        props: {
            sites: {
              type: [Array, Object],
              required: false
            },

            showSiteData: {
                type: Boolean,
                default: false
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                // Mirror of the sites prop. The reloadSites handler below refreshes
                // this list from the store, which it cannot do to a prop.
                localSites: this.sites,
            }
        },

        watch: {
            sites(value) {
                this.localSites = value;
            }
        },

        mounted() {
            this.$eventHub.$on('reloadSites', async () => {
                await this.$store.dispatch('loadSites');

                this.localSites = this.$store.getters.sitesSortedBy('building_address_line_1');
            });
        },

        computed: {
            activeSites() {
                return _.sortBy(_.filter(this.localSites, ['archived_at', null]), 'building.address_line_1');
            },

            keyDates() {
                return _.flatten(_.map(this.activeSites, (site) => {
                    return {
                        site: site,
                        leases: _.map(site.leases, (lease) => {
                            return {
                                name: lease.name,
                                dates: lease.key_dates
                            }
                        }),
                    }
                }));
            },

            leaseCount() {
                return _.sum(_.flatMap(this.keyDates, (keyDate) => {
                    return keyDate.leases?.length;
                }));
            },

            keyDateCount() {
                return _.sum(_.flatMap(this.keyDates, (keyDate) => {
                    return _.map(keyDate.leases, (lease) => {
                        return lease.dates?.length || 0;
                    });
                }));
            }
        },

        methods: {
            manageKeyDates() {
                this.$modal.show('manage-key-dates');
            }
        }
    }
</script>